// The prototype's small components (prototype/admin/src/05-ui.js), as React with the same
// markup and class names, so the CSS from @xr/ui/css/admin.css applies unchanged.
import { ADMIN_ICONS, DIRHAM_PATH, DIRHAM_VIEWBOX } from '@xr/ui';
import { type ReactNode, useEffect } from 'react';
import { L } from '../lib/prefs';
import { createStore, useStoreOf } from '../lib/store';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

export function Ic({ n, cls }: { n: string; cls?: string }) {
  return (
    <svg className={cx('ic', cls)} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: ADMIN_ICONS[n] || ADMIN_ICONS.dot || '' }} />
  );
}

type BtnOpts = { kind?: 'primary' | 'ghost' | 'danger' | 'danger-ghost' | ''; sm?: boolean; cls?: string; icon?: string; disabled?: boolean; why?: string; title?: string; id?: string; busy?: boolean; type?: 'button' | 'submit' };
export function Btn({ children, onClick, kind, sm, cls, icon, disabled, why, title, id, busy, type = 'button' }: BtnOpts & { children?: ReactNode; onClick?: () => void }) {
  const off = disabled || busy;
  return (
    <button type={type} className={cx('btn', kind, sm && 'sm', cls)} id={id} aria-disabled={off ? 'true' : undefined} aria-busy={busy ? 'true' : undefined}
      title={disabled ? why || '' : title} onClick={e => { if (off) { e.preventDefault(); return; } onClick?.(); }}>
      {icon ? <Ic n={icon} /> : null}
      {children != null && children !== '' ? <span>{children}</span> : null}
    </button>
  );
}

export function IBtn({ icon, onClick, title, cls, disabled, dot }: { icon: string; onClick?: () => void; title: string; cls?: string; disabled?: boolean; dot?: boolean }) {
  return (
    <button type="button" className={cx('ibtn', cls)} title={title} aria-label={title} aria-disabled={disabled ? 'true' : undefined}
      onClick={() => { if (!disabled) onClick?.(); }}>
      <Ic n={icon} />
      {dot ? <i className="dot" /> : null}
    </button>
  );
}

export const Kbd = ({ k }: { k: string }) => <kbd>{k}</kbd>;

export const ROLE_HUE: Record<string, number> = { owner: 168, admin: 205, finance: 262, supervisor: 32, collector: 140, auditor: 330 };
export const initials = (name: string) => name.split(' ').filter(Boolean).map(x => x[0]).slice(0, 2).join('');
export function Avatar({ name, role, size }: { name: string; role?: string; size?: string }) {
  return <span className={cx('av', size)} style={{ ['--h' as string]: ROLE_HUE[role || 'collector'] ?? 180 }} aria-hidden="true">{initials(name)}</span>;
}

export function Empty({ icon, title, body, action }: { icon: string; title: ReactNode; body?: ReactNode; action?: ReactNode }) {
  return <div className="empty"><Ic n={icon} /><h3>{title}</h3>{body ? <p>{body}</p> : null}{action}</div>;
}

export function Card({ title, children, cls, id, sub, tools, foot }: { title?: ReactNode; children?: ReactNode; cls?: string; id?: string; sub?: ReactNode; tools?: ReactNode; foot?: ReactNode }) {
  return (
    <section className={cx('card', cls)} id={id}>
      {title ? <header className="c-head"><div><h2>{title}</h2>{sub ? <p>{sub}</p> : null}</div>{tools ? <div className="c-tools">{tools}</div> : null}</header> : null}
      <div className="c-body">{children}</div>
      {foot ? <footer className="c-foot">{foot}</footer> : null}
    </section>
  );
}

export function PageHead({ title, sub, tools, crumb }: { title: ReactNode; sub?: ReactNode; tools?: ReactNode; crumb?: ReactNode }) {
  return (
    <div className="p-head">
      <div className="p-title">{crumb ? <div className="crumb">{crumb}</div> : null}<h1>{title}</h1>{sub ? <p>{sub}</p> : null}</div>
      {tools ? <div className="p-tools">{tools}</div> : null}
    </div>
  );
}

export function Seg<K extends string>({ items, cur, onPick }: { items: [K, string][]; cur: K; onPick: (k: K) => void }) {
  return (
    <div className="seg" role="radiogroup">
      {items.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={k === cur} onClick={() => onPick(k)}>{l}</button>)}
    </div>
  );
}

export function Field({ label, children, cls, htmlFor, req, hint, err }: { label?: ReactNode; children: ReactNode; cls?: string; htmlFor?: string; req?: boolean; hint?: ReactNode; err?: ReactNode }) {
  return (
    <div className={cx('field', cls)}>
      {label ? <label htmlFor={htmlFor}>{label}{req ? <> <b className="req">*</b></> : null}</label> : null}
      {children}
      {hint ? <p className="hint">{hint}</p> : null}
      {err ? <p className="err"><Ic n="alert" />{err}</p> : null}
    </div>
  );
}

export function Meter({ v, max, tone }: { v: number; max: number; tone?: string }) {
  const p = max ? Math.max(0, Math.min(100, (v / max) * 100)) : 0;
  const t = tone || (p >= 100 ? 'bad' : p >= 85 ? 'warn' : '');
  return <div className={cx('meter', t)} role="meter" aria-valuenow={v} aria-valuemax={max}><i style={{ width: p + '%' }} /></div>;
}

export function Note({ children, tone = 'info', icon }: { children: ReactNode; tone?: 'info' | 'warn' | 'bad' | 'good'; icon?: string }) {
  return <div className={cx('note', tone)}><Ic n={icon || (tone === 'warn' || tone === 'bad' ? 'alert' : tone === 'good' ? 'check' : 'info')} /><div>{children}</div></div>;
}

/* View state shared by the shell: one menu open at a time, the narrow-screen menu, the open modal. */
export const view = createStore({ menu: null as string | null, navOpen: false });
export const useView = () => useStoreOf(view);
export const closeMenu = () => view.set({ menu: null });

export type MenuItem = { label: ReactNode; icon?: string; onClick?: () => void; disabled?: boolean; why?: string; danger?: boolean; kbd?: string } | '-' | false | null;
/** A small popover anchored to a trigger, like the prototype's menuBtn(). */
export function MenuBtn({ id, items = [], trigger, align, panel }: { id: string; items?: MenuItem[]; trigger: (toggle: () => void) => ReactNode; align?: string; panel?: () => ReactNode }) {
  const v = useView();
  const open = v.menu === id;
  const toggle = () => view.set({ menu: open ? null : id });
  return (
    <div className="mwrap">
      {trigger(toggle)}
      {open && panel ? panel() : null}
      {open && !panel ? (
        <div className={cx('menu', align)} role="menu">
          {items.filter(Boolean).map((it, i) => it === '-' ? <hr key={i} /> : it ? (
            <button key={i} type="button" role="menuitem" className={it.danger ? 'danger' : ''} aria-disabled={it.disabled ? 'true' : undefined} title={it.disabled ? it.why || '' : undefined}
              onClick={() => { if (it.disabled) return; closeMenu(); it.onClick?.(); }}>
              {it.icon ? <Ic n={it.icon} /> : null}<span>{it.label}</span>{it.kbd ? <kbd>{it.kbd}</kbd> : null}
            </button>
          ) : null)}
        </div>
      ) : null}
    </div>
  );
}

/** Closes an open menu on a click outside it, and menus/the narrow menu on Escape (prototype behaviour). */
export function useMenuDismiss() {
  useEffect(() => {
    const click = (e: MouseEvent) => {
      const t = e.target as Element;
      if (view.get().menu && !t.closest('.menu') && !t.closest('.mwrap')) closeMenu();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (view.get().menu) return closeMenu();
      if (view.get().navOpen) view.set({ navOpen: false });
    };
    document.addEventListener('click', click);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('click', click); document.removeEventListener('keydown', key); };
  }, []);
}

/** The placeholder QR the prototype draws (rng 42). The real store links come with the app listing. */
export function FakeQR() {
  let a = 42 >>> 0;
  const r = () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const cells: ReactNode[] = [];
  for (let y = 0; y < 29; y++) for (let x = 0; x < 29; x++) {
    const f = (x < 8 && y < 8) || (x > 20 && y < 8) || (x < 8 && y > 20);
    if (f) continue;
    if (r() > 0.52) cells.push(<rect key={x + '-' + y} x={x} y={y} width="1" height="1" className="qr-d" />);
  }
  return (
    <svg viewBox="0 0 29 29" className="qr" aria-hidden="true">
      <rect width="29" height="29" className="qr-bg" />
      {cells}
      {[[0, 0], [21, 0], [0, 21]].map(([x = 0, y = 0]) => (
        <g key={x + ':' + y}><rect x={x + 0.5} y={y + 0.5} width="7" height="7" className="qr-o" /><rect x={x + 2} y={y + 2} width="4" height="4" className="qr-d" /></g>
      ))}
    </svg>
  );
}

export const tClose = () => L('Close', 'إغلاق');

export function Switch({ id, on, onToggle, disabled, why, label }: { id: string; on: boolean; onToggle: () => void; disabled?: boolean; why?: string; label: string }) {
  return (
    <button type="button" role="switch" id={id} className="switch" aria-checked={!!on} aria-disabled={disabled ? 'true' : undefined} title={disabled ? why || '' : undefined} aria-label={label}
      onClick={() => { if (!disabled) onToggle(); }}><i /></button>
  );
}

export function Select<V extends string | number>({ id, value, opts, onChange, disabled, label }: { id: string; value: V; opts: [V, string][]; onChange: (v: V) => void; disabled?: boolean; label?: string }) {
  return (
    <select id={id} className="inp sel" value={String(value)} disabled={disabled} aria-label={label || ''}
      onChange={e => { const o = opts.find(([v]) => String(v) === e.target.value); if (o) onChange(o[0]); }}>
      {opts.map(([v, l]) => <option key={String(v)} value={String(v)}>{l}</option>)}
    </select>
  );
}

export function Radios<V extends string>({ name, value, opts, onChange, disabled, cls }: { name: string; value: V; opts: ([V, string] | [V, string, string])[]; onChange: (v: V) => void; disabled?: boolean; cls?: string }) {
  return (
    <div className={cx('radios', cls)} role="radiogroup">
      {opts.map(([v, l, d]) => (
        <label key={v} className={cx('rad', v === value && 'on', disabled && 'dis')}>
          <input type="radio" name={name} value={v} checked={v === value} disabled={disabled} onChange={() => onChange(v)} />
          <span className="dotr" />
          <span><b>{l}</b>{d ? <small>{d}</small> : null}</span>
        </label>
      ))}
    </div>
  );
}

/** A currency label: the dirham symbol for AED (as dirham.js draws a bare "AED"), else the code. */
export function Cur({ c }: { c?: string | null }) {
  if ((c || 'AED') !== 'AED') return <>{c}</>;
  return <span className="dh" role="img" aria-label="AED"><svg className="dh-s" viewBox={DIRHAM_VIEWBOX} aria-hidden="true" focusable="false"><path d={DIRHAM_PATH} /></svg></span>;
}
